//* Importing React and useState, useCallback hooks from React
import { useState, useCallback } from "react";

//* Importing Libraries from React and react-query
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//* Importing API functions from registrationAPI.ts
import { registrationAPI } from "./registrationAPI";

//? Importing types from types.ts
import {
  type RegistrationDetails,
  type CreateRegistrationInput,
  type UpdateRegistrationInput,
  type Pagination,
  type RegistrationResponse,
} from "./types/types";
/**
 * Hook customizado para gerenciar operações de matrículas.
 *
 * @param token - Token de autenticação para as requisições API.
 * @param teamId - (Opcional) ID da turma para filtrar os registros.
 * @param customPerPage - (Opcional) Número de registros por página. Se definido, substitui o padrão.
 * @returns Funções e estados relacionados às matrículas.
 */
export const useRegistrations = (
  token: string,
  teamId?: string,
  customPerPage?: number
) => {
  const queryClient = useQueryClient();
  // State para controlar o polling de confirmação de múltiplos registros
  const [isPollingActive, setIsPollingActive] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: customPerPage || 10,
    totalItems: 0,
  });
  const [filters, setFilters] = useState<{
    teamId?: string;
    unitId?: string;
    courseId?: string;
    isActive?: boolean;
  }>({ teamId });

  const queryKey = [
    "registrations",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
      ...filters,
    },
  ] as const;

  const { data, isLoading, isError, error, isFetching, refetch } =
    useQuery<RegistrationResponse>({
      queryKey,
      queryFn: () =>
        registrationAPI.getAll(
          search,
          pagination.currentPage,
          token,
          pagination.itemsPerPage,
          filters.teamId,
          filters.unitId,
          filters.courseId,
          filters.isActive
        ),
      staleTime: 5 * 60 * 1000,
      refetchInterval: isPollingActive ? 2000 : false,
    });

  const registrations = data?.registrations || [];
  const fetchedPagination = data?.pagination || pagination;

  // Atualiza a busca e reseta para a primeira página
  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  // Atualiza os filtros e reseta para a primeira página
  const handleFilters = useCallback(
    (
      newFilters: Partial<{
        teamId?: string;
        unitId?: string;
        courseId?: string;
        isActive?: boolean;
      }>
    ) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        ...newFilters,
      }));
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    },
    []
  );

  // Altera a página atual
  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  // Mutação para criar registro
  const createRegistrationMutation = useMutation({
    mutationFn: (registrationData: CreateRegistrationInput) =>
      registrationAPI.create(registrationData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["registrations"],
        exact: false,
      });
    },
  });

  // Mutação para atualizar uma matrícula
  const updateRegistrationMutation = useMutation({
    mutationFn: ({ id, ...registrationData }: UpdateRegistrationInput) =>
      registrationAPI.update(id, registrationData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["registrations"],
        exact: false,
      });
    },
  });

  // Mutação para deletar uma matrícula
  const deleteRegistrationMutation = useMutation({
    mutationFn: (id: string) => registrationAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["registrations"],
        exact: false,
      });
    },
  });

  // Mutação para confirmar uma matrícula individual
  const confirmRegistrationMutation = useMutation({
    mutationFn: (id: string) => registrationAPI.confirm(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["registrations"],
        exact: false,
      });
    },
  });

  // Função de polling que verifica se as matrículas confirmadas já estão atualizadas
  const pollRegistrationsStatus = async (
    selectedIds: string[]
  ): Promise<boolean> => {
    const maxAttempts = 5;
    let attempts = 0;
    let allConfirmed = false;

    while (!allConfirmed && attempts < maxAttempts) {
      // Aguarda 2 segundos antes de refazer a consulta
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Força a atualização dos dados
      await refetch();

      // Obtém os dados atuais do cache
      const currentData =
        queryClient.getQueryData<RegistrationResponse>(queryKey);
      if (currentData && currentData.registrations) {
        allConfirmed = selectedIds.every((id) => {
          const registration = currentData.registrations.find(
            (reg) => reg.id === id
          );
          return registration && registration.isActive === true;
        });
      }
      attempts++;
    }

    return allConfirmed;
  };

  // Mutação para confirmar múltiplas matrículas com polling
  const confirmMultipleMutation = useMutation({
    mutationFn: (ids: string[]) =>
      registrationAPI.confirmMultipleAsync(ids, token),
    onSuccess: async (_data, selectedIds) => {
      setIsPollingActive(true); 
      
      // Invalidate the queries to force the refetch
      queryClient.invalidateQueries({
        queryKey: ["registrations"],
        exact: false,
      });

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const allConfirmed = await pollRegistrationsStatus(selectedIds);
      setIsPollingActive(false);
      await refetch();
    },
  });

  // Busca registro por ID
  const getRegistrationById = useCallback(
    async (id: string): Promise<RegistrationDetails | null> => {
      try {
        const registrationDetails = await registrationAPI.getById(id, token);
        return registrationDetails;
      } catch (err) {
        console.error("Erro ao buscar registro por ID:", err);
        return null;
      }
    },
    [token]
  );

  // Atualiza os filtros (mesma lógica do handleFilters)
  const updateFilters = useCallback(
    (
      newFilters: Partial<{
        teamId?: string;
        unitId?: string;
        courseId?: string;
        isActive?: boolean;
      }>
    ) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        ...newFilters,
      }));
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    },
    []
  );

  return {
    registrations,
    loading: isLoading || isFetching,
    error: isError ? (error as Error)?.message : null,
    search,
    setSearch: handleSearch,
    filters,
    setFilters: handleFilters,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createRegistration: createRegistrationMutation.mutate,
    updateRegistration: updateRegistrationMutation.mutate,
    deleteRegistration: deleteRegistrationMutation.mutate,
    getRegistrationById,
    confirmRegistration: confirmRegistrationMutation.mutate,
    confirmMultipleRegistrations: confirmMultipleMutation.mutate,
    changePage,
    updateFilters,
    isCreating: createRegistrationMutation.isPending,
    isUpdating: updateRegistrationMutation.isPending,
    isDeleting: deleteRegistrationMutation.isPending,
    isConfirming: confirmRegistrationMutation.isPending,
    isConfirmingMultiple: confirmMultipleMutation.isPending,
  };
};
